import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { StaffFilters, getStaffClasses } from '@/store/slices/staffClassesSlice';
import { GymLocation, MyClass, StaffClassesResponse, StaffInstructor } from '@/types/api';
import { useChicagoSeconds } from '@/hooks/useChicagoSeconds';
import { chicagoToday } from '@/utils/scheduleWeek';
import {
  addDays,
  chicagoSeconds,
  focusRotation,
  formatClockTime,
  formatRemaining,
  parseHHMM,
} from '@/utils/rotations';
import { DayPicker, LOCATION_NAMES } from '@/components/classes/ClassDay';
import { colors, font, space, radius } from '@/theme/chalkCourt';

/** Hour chips: the gym's class hours (Saturday starts ~8, last classes end ~9 PM). */
export const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM … 9 PM

export const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;

/** The current Chicago clock hour, or null (all day) outside the gym's hours. */
export const currentHourChip = (now: Date = new Date()): number | null => {
  const h = Math.floor(chicagoSeconds(now) / 3600);
  return HOURS.includes(h) ? h : null;
};

/**
 * The filters to (re)fetch with now: a list held from an earlier day goes back
 * to today and the current hour; a "follow now" view moves to the current hour.
 */
export const freshStaffFilters = (
  f: StaffFilters,
  list: StaffClassesResponse | null,
  now: Date = new Date()
): StaffFilters => {
  const hour = currentHourChip(now);
  if (list && list.today !== chicagoToday(now)) {
    return { location: f.location ?? list.location, date: null, hour, followNow: true };
  }
  return f.followNow ? { ...f, date: null, hour } : f;
};

/**
 * The moment a row describes: now when looking at today's current hour (or
 * today's whole day), else the start of the chosen hour (or of the class).
 */
const referenceSec = (cls: MyClass, hour: number | null, live: boolean, nowSec: number): number => {
  if (live) return nowSec;
  const classStart = parseHHMM(cls.start) * 60;
  return hour === null ? classStart : Math.max(hour * 3600, classStart);
};

const InstructorRow: React.FC<{
  instructor: StaffInstructor;
  hour: number | null;
  live: boolean;
  nowSec: number;
  onPress: () => void;
}> = ({ instructor, hour, live, nowSec, onPress }) => {
  const cls = instructor.classes[0];
  const ref = referenceSec(cls, hour, live, nowSec);
  const focus = focusRotation(cls, ref);
  const more = instructor.classes.length - 1;
  return (
    <TouchableOpacity
      style={styles.row}
      activeOpacity={0.8}
      onPress={onPress}
      testID={`instructor-${instructor.employeeId}`}>
      <View style={styles.rowMain}>
        <Text style={styles.rowName} numberOfLines={1}>
          {instructor.name}
        </Text>
        <Text style={styles.rowClass} numberOfLines={1}>
          {cls.name} · {formatClockTime(cls.start)}–{formatClockTime(cls.end)}
        </Text>
        {focus ? (
          <Text
            style={[styles.rowRotation, focus.running && live && styles.rowRotationLive]}
            numberOfLines={1}>
            {focus.rotation.apparatus} · {focus.rotation.station}
            {focus.running && live
              ? ` · ${formatRemaining(parseHHMM(focus.rotation.end) * 60 - nowSec)} left`
              : focus.running
              ? ''
              : ` · from ${formatClockTime(focus.rotation.start)}`}
          </Text>
        ) : (
          <Text style={styles.rowNoRotation}>
            {cls.rotations.length === 0 ? 'No rotations' : 'Rotations finished'}
          </Text>
        )}
        {more > 0 && (
          <Text style={styles.rowMore}>
            +{more} more {more === 1 ? 'class' : 'classes'}
          </Text>
        )}
      </View>
      <Icon name="chevron-forward" size={18} color={colors.inkFaint} />
    </TouchableOpacity>
  );
};

/**
 * Admin "Staff" view of the Classes tab: pick a gym, a day of this week and
 * an hour (defaults: home gym, today, the current hour) to see who's teaching
 * and where their class is in its rotations; search narrows by staff or class
 * name; tap someone to see their whole day.
 */
const StaffClassesView: React.FC<{
  onOpenStaff: (instructor: StaffInstructor, date: string | null) => void;
}> = ({ onOpenStaff }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { list, filters, isLoading, error } = useSelector((s: RootState) => s.staffClasses);
  const [query, setQuery] = useState('');

  // First visit: home gym, today, the current hour.
  useEffect(() => {
    if (!filters)
      dispatch(
        getStaffClasses({ location: null, date: null, hour: currentHourChip(), followNow: true })
      );
  }, [filters, dispatch]);

  const apply = (patch: Partial<StaffFilters>) => {
    const base: StaffFilters = filters ?? { location: null, date: null, hour: currentHourChip() };
    const next = { ...base, location: base.location ?? list?.location ?? null, ...patch };
    // Picking today's current hour (or keeping it) follows "now"; any other choice —
    // including an explicit "All day" outside class hours — pins.
    next.followNow = next.date === null && next.hour !== null && next.hour === currentHourChip();
    dispatch(getStaffClasses(next));
  };
  const refresh = () => filters && dispatch(getStaffClasses(freshStaffFilters(filters, list)));

  const heldIsToday = !!list && list.date === list.today;
  const nowSec = useChicagoSeconds(heldIsToday);
  const deviceToday = chicagoToday();
  const isToday = heldIsToday && list!.today === deviceToday;
  const nowHour = Math.floor(nowSec / 3600);

  // While following "now", move with the clock: a new hour, or a new day
  // (at most one refetch per device date, so a device/server date mismatch can't loop).
  const followedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!filters || !list || isLoading || error) return;
    const fresh = freshStaffFilters(filters, list);
    if (
      fresh.date === filters.date &&
      fresh.hour === filters.hour &&
      fresh.location === filters.location
    )
      return;
    const key = `${deviceToday}|${fresh.hour}`;
    if (followedFor.current === key) return;
    followedFor.current = key;
    dispatch(getStaffClasses(fresh));
  }, [filters, list, isLoading, error, nowHour, deviceToday, dispatch]);
  const hour = filters ? filters.hour : null;
  // Live = the rows describe this moment (today, and the current hour or all day).
  const live = isToday && (hour === null || hour === nowHour);

  const location: GymLocation | null = filters?.location ?? list?.location ?? null;
  const target = list ? filters?.date ?? list.today : null;
  const canPrev = !!list && target! > list.weekStart;
  const canNext = !!list && target! < list.weekEnd;
  const goDay = (day: string) => list && apply({ date: day === list.today ? null : day });

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!list || !q) return list?.instructors ?? [];
    return list.instructors.filter(
      (i) =>
        i.name.toLowerCase().includes(q) || i.classes.some((c) => c.name.toLowerCase().includes(q))
    );
  }, [list, query]);

  return (
    <View>
      {/* Gym */}
      <View style={styles.segment}>
        {(['SL', 'RM'] as GymLocation[]).map((loc) => (
          <TouchableOpacity
            key={loc}
            style={[styles.segmentBtn, location === loc && styles.segmentBtnOn]}
            onPress={() => location !== loc && apply({ location: loc })}
            testID={`location-${loc}`}>
            <Text style={[styles.segmentText, location === loc && styles.segmentTextOn]}>
              {LOCATION_NAMES[loc]}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <DayPicker
        date={list ? list.date : null}
        isToday={isToday}
        canPrev={canPrev}
        canNext={canNext}
        onPrev={() => target && goDay(addDays(target, -1))}
        onNext={() => target && goDay(addDays(target, 1))}
        onToday={() => list && goDay(list.today)}
      />

      {/* Hour */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.hours}>
        <TouchableOpacity
          style={[styles.chip, hour === null && styles.chipOn]}
          onPress={() => hour !== null && apply({ hour: null })}
          testID="hour-all">
          <Text style={[styles.chipText, hour === null && styles.chipTextOn]}>All day</Text>
        </TouchableOpacity>
        {HOURS.map((h) => (
          <TouchableOpacity
            key={h}
            style={[styles.chip, hour === h && styles.chipOn]}
            onPress={() => hour !== h && apply({ hour: h })}
            testID={`hour-${h}`}>
            <Text style={[styles.chipText, hour === h && styles.chipTextOn]}>
              {isToday && h === nowHour ? `Now · ${hourLabel(h)}` : hourLabel(h)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <View style={styles.search}>
        <Icon name="search" size={18} color={colors.inkFaint} />
        <TextInput
          style={styles.searchInput}
          value={query}
          onChangeText={setQuery}
          placeholder="Search staff or class"
          placeholderTextColor={colors.inkFaint}
          autoCorrect={false}
          autoCapitalize="none"
          testID="staff-search"
        />
      </View>

      {!list && isLoading && <ActivityIndicator style={styles.loading} color={colors.accent} />}
      {error && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={refresh}>
            <Text style={styles.retry}>Try again</Text>
          </TouchableOpacity>
        </View>
      )}

      {list && (
        <>
          <Text style={styles.sectionTitle}>
            {hour === null
              ? 'TEACHING TODAY'
              : `TEACHING ${hourLabel(hour)}–${hourLabel((hour + 1) % 24)}`}
            {' · '}
            {shown.length}
          </Text>
          {shown.length === 0 ? (
            <Text style={styles.empty}>
              {list.instructors.length === 0
                ? list.dayCode === null
                  ? 'No classes on Sundays.'
                  : `No one is teaching at ${LOCATION_NAMES[list.location]} ${
                      hour === null ? 'this day' : 'in this hour'
                    }.`
                : 'No staff match your search.'}
            </Text>
          ) : (
            shown.map((i) => (
              <InstructorRow
                key={i.employeeId}
                instructor={i}
                hour={hour}
                live={live}
                nowSec={nowSec}
                onPress={() => onOpenStaff(i, list.date === list.today ? null : list.date)}
              />
            ))
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  segment: {
    flexDirection: 'row',
    backgroundColor: colors.surfaceSunk,
    borderRadius: radius.pill,
    padding: 3,
    marginBottom: space.sm,
  },
  segmentBtn: {
    flex: 1,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    alignItems: 'center',
  },
  segmentBtnOn: { backgroundColor: colors.ink },
  segmentText: { fontFamily: font.bodySemi, fontSize: 14, color: colors.inkSoft },
  segmentTextOn: { color: colors.surface },

  hours: { paddingBottom: space.md, gap: space.sm },
  chip: {
    paddingVertical: 6,
    paddingHorizontal: space.md,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.surface,
  },
  chipOn: { backgroundColor: colors.accent, borderColor: colors.accent },
  chipText: { fontFamily: font.bodySemi, fontSize: 14, color: colors.ink },
  chipTextOn: { color: colors.onAccent },

  search: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  searchInput: {
    flex: 1,
    fontFamily: font.body,
    fontSize: 16,
    color: colors.ink,
    paddingVertical: space.sm,
    marginLeft: space.sm,
  },

  loading: { marginTop: space.xl },
  errorBox: {
    backgroundColor: colors.accentSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
  },
  errorText: { fontFamily: font.body, fontSize: 14, color: colors.accentDark },
  retry: { fontFamily: font.bodyBold, fontSize: 14, color: colors.accentDark, marginTop: space.sm },

  sectionTitle: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2,
    color: colors.inkSoft,
    marginTop: space.sm,
    marginBottom: space.sm,
  },
  empty: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  rowMain: { flex: 1, marginRight: space.sm },
  rowName: { fontFamily: font.bodyBold, fontSize: 17, color: colors.ink },
  rowClass: { fontFamily: font.bodyMed, fontSize: 14, color: colors.inkSoft, marginTop: 1 },
  rowRotation: { fontFamily: font.bodySemi, fontSize: 14, color: colors.ink, marginTop: 2 },
  rowRotationLive: { color: colors.court },
  rowNoRotation: { fontFamily: font.body, fontSize: 13, color: colors.inkFaint, marginTop: 2 },
  rowMore: { fontFamily: font.body, fontSize: 12, color: colors.inkFaint, marginTop: 2 },
});

export default StaffClassesView;
